import SenderSelect from './SenderSelect';   // [SMS_SENDER_PICK_V1] 발신번호 드롭다운
import React, { useEffect, useState } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────
// SMS 발송관리 (관리탭 > 관리자 전용)
//  - 일정 종류별 발송 시기 / 시각 / 문구 / 대상 설정
//  - 발송 이력 조회
// ─────────────────────────────────────────────

const TYPE_LABEL = {
  deposit: '계약금',
  middle: '중도금',
  balance: '잔금',
  expiry: '임대차 만료',
};

const UNIT_LABEL = { month: '개월', week: '주', day: '일', hour: '시간' };

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function SmsRuleManager({ cardStyle, titleStyle, subTitleStyle }) {
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [msg, setMsg] = useState('');
  // 카드별 저장 결과 (event_type → 메시지). 어느 항목을 저장했는지 바로 보이게 한다
  const [saveMsg, setSaveMsg] = useState({});
  const [openLogs, setOpenLogs] = useState(false);
  const [ctSender, setCtSender] = useState('');   // [SMS_SENDER_PICK_V1] 계약 SMS 발신번호 (저장값)

  useEffect(() => { loadRules(); }, []);

  const loadRules = async () => {
    try {
      const res = await axios.get('/api/contract-sms/rules', auth());
      if (res.data.success) setRules(res.data.data);
    } catch (e) {
      setMsg('규칙을 불러오지 못했습니다');
    }
  };

  const loadLogs = async () => {
    try {
      const res = await axios.get('/api/contract-sms/logs?limit=50', auth());
      if (res.data.success) setLogs(res.data.data);
      setOpenLogs(true);
    } catch (e) {
      setMsg('이력을 불러오지 못했습니다');
    }
  };

  // 화면상에서만 값 변경 (저장은 버튼 클릭 시)
  const change = (idx, field, value) => {
    const next = [...rules];
    next[idx] = { ...next[idx], [field]: value };
    setRules(next);
  };

  const save = async (r) => {
    try {
      const res = await axios.put(`/api/contract-sms/rules/${r.event_type}`, {
        offset_value: r.offset_value,
        offset_unit: r.offset_unit,
        offset_dir: r.offset_dir,
        send_time: r.send_time,
        message_template: r.message_template,
        target_landlord: r.target_landlord,
        target_tenant: r.target_tenant,
        enabled: r.enabled,
      }, auth());
      setSaveMsg((m) => ({ ...m, [r.event_type]: res.data.success ? '✅ 저장 완료' : '❌ 저장 실패' }));
      setTimeout(() => setSaveMsg((m) => ({ ...m, [r.event_type]: '' })), 3000);
    } catch (e) {
      setSaveMsg((m) => ({ ...m, [r.event_type]: '❌ ' + (e.response?.data?.message || e.message) }));
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={titleStyle}>📨 SMS 발송관리</h3>
      <p style={subTitleStyle}>일정별 자동 문자 발송 조건을 설정합니다.</p>

      {/* [SMS_SENDER_PICK_V1] 계약 SMS 발신번호 — 바꾸면 바로 저장, 이후 정각 자동·즉시·재발송 모두 이 번호 */}
      <SenderSelect kind="contract" value={ctSender} onChange={setCtSender} style={{ marginTop: '8px' }} />

      {rules.map((r, i) => (
        <div key={r.event_type} style={{
          marginTop: '12px', padding: '10px',
          border: '1px solid #e2e8f0', borderRadius: '6px',
          backgroundColor: r.enabled ? '#fff' : '#f8f8f8',
        }}>
          {/* 종류 + 사용여부 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: '13px' }}>{TYPE_LABEL[r.event_type]}</strong>
            <label style={{ fontSize: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!r.enabled}
                onChange={(e) => change(i, 'enabled', e.target.checked ? 1 : 0)}
                style={{ marginRight: '4px' }}
              />
              사용
            </label>
          </div>

          {/* 발송 시기 */}
          <div style={{ display: 'flex', gap: '4px', marginTop: '8px', alignItems: 'center' }}>
            <input
              type="number"
              min="0"
              value={r.offset_value}
              onChange={(e) => change(i, 'offset_value', e.target.value)}
              style={{ width: '52px', padding: '5px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '12px' }}
            />
            <select
              value={r.offset_unit}
              onChange={(e) => change(i, 'offset_unit', e.target.value)}
              style={{ padding: '5px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '12px' }}
            >
              {Object.entries(UNIT_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              value={r.offset_dir}
              onChange={(e) => change(i, 'offset_dir', e.target.value)}
              style={{ padding: '5px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '12px' }}
            >
              <option value="before">전</option>
              <option value="after">후</option>
            </select>
            <input
              type="time"
              value={String(r.send_time || '09:00:00').slice(0, 5)}
              onChange={(e) => change(i, 'send_time', e.target.value + ':00')}
              style={{ padding: '5px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '12px' }}
            />
          </div>

          {/* 발송 대상 */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '12px' }}>
            <label style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!r.target_landlord}
                onChange={(e) => change(i, 'target_landlord', e.target.checked ? 1 : 0)}
                style={{ marginRight: '4px' }}
              />
              임대(매도)인
            </label>
            <label style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!r.target_tenant}
                onChange={(e) => change(i, 'target_tenant', e.target.checked ? 1 : 0)}
                style={{ marginRight: '4px' }}
              />
              임차(매수)인
            </label>
          </div>

          {/* 문구 */}
          <textarea
            value={r.message_template || ''}
            onChange={(e) => change(i, 'message_template', e.target.value)}
            placeholder="{이름} {소재지} {일정명} {날짜} 사용 가능"
            style={{
              width: '100%', marginTop: '8px', padding: '6px', minHeight: '60px',
              border: '1px solid #e2e8f0', borderRadius: '3px',
              fontSize: '12px', fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px' }}>
            치환: {'{이름} {소재지} {일정명} {날짜}'}
          </div>

          <button
            onClick={() => save(r)}
            style={{
              marginTop: '8px', backgroundColor: '#2563eb', color: 'white',
              border: 'none', padding: '6px 14px', borderRadius: '4px',
              cursor: 'pointer', fontSize: '12px',
            }}
          >
            저장
          </button>
          {saveMsg[r.event_type] && (
            <span style={{ fontSize: '11px', marginLeft: '8px', color: '#64748b' }}>
              {saveMsg[r.event_type]}
            </span>
          )}
        </div>
      ))}

      {/* 이력 */}
      <button
        onClick={openLogs ? () => setOpenLogs(false) : loadLogs}
        style={{
          marginTop: '14px', width: '100%', backgroundColor: '#f1f5f9',
          border: '1px solid #e2e8f0', padding: '7px', borderRadius: '4px',
          cursor: 'pointer', fontSize: '12px',
        }}
      >
        {openLogs ? '발송 이력 닫기' : '📋 발송 이력 보기'}
      </button>

      {openLogs && (
        <div style={{ marginTop: '10px', maxHeight: '260px', overflowY: 'auto' }}>
          {logs.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#94a3b8', padding: '8px' }}>
              발송 이력이 없습니다.
            </div>
          ) : logs.map((l) => (
            <div key={l.log_id} style={{
              fontSize: '11px', padding: '7px', marginBottom: '4px',
              backgroundColor: '#f8fafc', borderRadius: '3px',
              borderLeft: `3px solid ${l.status === 'success' ? '#4CAF50' : '#f44336'}`,
            }}>
              <div>
                <strong>{TYPE_LABEL[l.event_type] || l.event_type}</strong>
                {' · '}{l.recipient_name} ({l.recipient_phone})
                {' · '}{l.status === 'success' ? '성공' : '실패'}
              </div>
              <div style={{ color: '#64748b', marginTop: '2px' }}>
                {String(l.sent_at).replace('T', ' ').slice(0, 16)}
                {l.location_full_address ? ` · ${l.location_full_address}` : ''}
              </div>
              {l.error_msg && (
                <div style={{ color: '#dc2626', marginTop: '2px' }}>{l.error_msg}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {msg && (
        <p style={{ fontSize: '11px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
          {msg}
        </p>
      )}
    </div>
  );
}
